import { UploadFile } from 'antd/es/upload/interface';
import EXIF from 'exif-js/exif';
import JsPDF from 'jspdf';
import { FONT_NANUM_GOTHIC } from '@/utils/fonts';
import { pcompany, ptax } from '@/proto/proto';
import { first } from '@/utils/collectionExtension';
import moment from 'moment';
import { FORMAT_DATE_EXCEPT_SECOND_KR, FORMAT_DATE_FORMAL_KR } from '@/constants/constant';
import JSZip from 'jszip';
import FileSaver from 'file-saver';
import PdfDocumentFont from '@/utils/pdf/PdfDocumentFont';

export const DEFAULT_WIDTH = 1080;
export const DEFAULT_HEIGHT = 1440;

export const progress = async (f: (percentage: number) => void) => {
  Array.from({ length: 10 }, (v, k) => k * 10).forEach((v) => f(v));
};

export const read = (file: UploadFile) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    if (file.xhr) {
      reader.readAsDataURL(file.xhr);
    } else {
      reject();
    }
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else if (reader.result instanceof ArrayBuffer) {
        const decoder = new TextDecoder();
        resolve(decoder.decode(reader.result));
      } else {
        reject();
      }
    };
    reader.onerror = (error) => reject(error);
  });

export const readBytes = (file: UploadFile) =>
  new Promise<number[]>((resolve, reject) => {
    const reader = new FileReader();
    if (file.originFileObj) {
      reader.readAsArrayBuffer(file.originFileObj);
    } else {
      reject();
    }
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        const array = new Uint8Array(reader.result);
        const fileByteArray = [];
        for (let i = 0; i < array.length; i += 1) {
          fileByteArray.push(array[i]);
        }
        resolve(fileByteArray);
      } else {
        reject();
      }
    };
    reader.onerror = (error) => reject(error);
  });

const changeSize = (width: number, height: number, maxWidth: number, maxHeight: number) => {
  if (width > maxWidth) {
    width = maxWidth;
    height = Math.round((height * maxWidth) / width);
  }
  if (height > maxHeight) {
    width = Math.round((width * maxHeight) / height);
    height = maxHeight;
  }
  return { width, height };
};

const resizeImage = (image: HTMLImageElement, width: number, height: number, format = 'jpeg') => {
  const canvas: HTMLCanvasElement = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return undefined;
  context.drawImage(image, 0, 0, width, height);
  return canvas.toDataURL(`image/${format}`);
};

export const base64ToBlob = (data: string | undefined | null, type = 'image/jpeg') => {
  if (!data) return null;
  const sliceSize = 512;
  const byteCharacters = atob(data.replace(/^data:image\/(png|jpeg|jpg);base64,/, ''));
  const byteArrays = [];
  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const bytes = new Array(slice.length);
    for (let i = 0; i < slice.length; i += 1) {
      bytes[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(bytes);
    byteArrays.push(byteArray);
  }
  return new Blob(byteArrays, { type });
};

export const blobToBase64 = (data: Blob | undefined | null) =>
  new Promise<string>((resolve, reject) => {
    if (!data) reject();
    else {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          const dataUrl = reader.result;
          const result = dataUrl.split(',')[1];
          if (result) {
            resolve(result);
          } else {
            reject();
          }
        }
      };
      reader.readAsDataURL(data);
    }
  });

const dataUrlToFile = (name: string, dataUrl: string) => {
  const base64Maker = ';base64,';

  // Base64로 인코딩 되지 않았을 때
  if (dataUrl.indexOf(base64Maker) === -1) {
    const parts = dataUrl.split(',');
    const contentType = parts[0].split(':')[1];
    const raw = parts[1];
    return new File([raw], name, {
      type: contentType,
    });
  }

  const parts = dataUrl.split(base64Maker);
  console.log(parts);
  const contentType = parts[0].split(':')[1];
  const raw = atob(parts[1]);
  const rawLength = raw.length;
  const data = new Uint8Array(rawLength);

  data.forEach((_, i) => {
    data[i] = raw.charCodeAt(i);
  });

  return new File([data], name, {
    type: contentType,
  });
};

export const bufferToBase64 = (data: ArrayBuffer | undefined | null) =>
  new Promise<string>((resolve, reject) => {
    if (!data) reject();
    else {
      resolve(btoa(new Uint8Array(data).reduce((total, byte) => data + String.fromCharCode(byte), '')));
    }
  });

export const resize = (file: File | undefined, width = DEFAULT_WIDTH, height = DEFAULT_HEIGHT, type = 'jpeg') =>
  new Promise<Blob>((resolve, reject) => {
    console.log(file?.type);
    if (file?.type !== 'image/jpeg') {
      resolve(file as Blob);
    }
    const reader = new FileReader();
    if (file) {
      reader.readAsDataURL(file);
    } else {
      reject();
    }
    reader.onload = () => {
      const image = new Image();
      if (typeof reader.result === 'string') {
        image.src = reader.result;
        image.onload = () => {
          const resized = resizeImage(image, width, height, type);
          if (file && resized) {
            resolve(dataUrlToFile(file.name, resized));
          } else {
            reject();
          }
        };
      } else {
        reject();
      }
    };
  });

export const compressAsync = (files: Promise<Blob>[], resultName: string) =>
  new Promise<JSZip | null>((resolve, reject) => {
    const zip = new JSZip();
    console.log(files);
    files[0].then((file) => {
      zip.file(resultName, file, { createFolders: true });
    });
    files.forEach(async (future) => {
      const file = await future;
      return zip.file(resultName, file, { createFolders: true });
    });
    console.log('compressed', zip);
    return zip
      .generateAsync({ type: 'blob', mimeType: 'application/octet-stream', platform: 'UNIX' }, (metadata) => {
        console.log('update', metadata);
      })
      .then((compressed) => {
        FileSaver.saveAs(compressed, `${resultName}.zip`);
        return resolve(zip);
      })
      .catch(reject);
  });

export const downloadFileFromUrl = (url: string, contentType: string, fileName: string) =>
  new Promise((resolve, reject) => {
    return fetch(url, {
      method: 'GET',
      headers: {
        'Contents-Type': contentType,
      },
    })
      .then((response) => response.blob())
      .then((blob) => {
        const objectUrl = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = objectUrl;
        link.setAttribute('download', fileName);

        document.body.appendChild(link);

        link.click();

        link.parentNode?.removeChild(link);
      })
      .then(resolve)
      .catch(reject);
  });

const ExifOrientations = {
  // ◾️️◾◾️️️◾◾️️◾️◾️◾
  // ◾️️▫️️▫️️▫️️▫️◾️️️️️️️▫️️◾
  // ◾️◾◾️️️◾▫️◾▫️️◾️
  // ◾️️◾◾️️️️️◾▫️️◾▫️▫️
  // ◾️️◾◾️️️️️◾▫️️◾▫️️◾
  // ◾️️◾◾️️️️️◾▫️️◾▫️️◾
  // ◾️️◾◾️️️️️◾▫️️◾▫️️◾
  // ◾️️◾◾️️️◾◾️️◾️◾️◾
  ORIGINAL: 1,

  // ◾◾️◾️️️◾◾️️◾️◾️️◾
  // ◾◾️◾️️️️️◾◾️️️️◾▫️️◾️
  // ◾◾️◾️️️️️◾◾️️️️◾▫️️◾️
  // ◾◾️◾️️️️️◾◾️️️️◾▫️️◾️
  // ◾▫️️▫️️️▫️️▫️️▫️️▫️◾
  // ◾️◾◾️️️◾◾️️◾️◾️️◾
  // ◾▫️▫️️▫️️▫️️▫️️▫️️◾️
  // ◾️◾◾️️️️️◾▫️️◾️◾️️◾️
  ROTATED_90: 8, // 시계방향으로 90도 회전

  // ◾️️◾◾️️️◾◾️️◾️◾️◾
  // ◾️️▫️◾️️▫️◾️️◾️️◾️️◾️
  // ◾️️▫️◾️️▫️◾️️◾️️◾️️◾️
  // ◾️️▫️◾️️▫️◾️️◾️️◾️️◾️
  // ▫️️️▫️️◾▫️️◾️️️️️◾️️◾️◾
  // ◾️️▫️◾️️▫️◾️️◾️️◾️️◾️
  // ◾▫️️️◾▫️▫️▫️▫️️️️️️◾
  // ◾️️◾◾️️️◾◾️️◾️◾️◾
  ROTATED_180: 3, // 시계방향으로 180도 회전

  // ◾◾️️️️️◾▫️️◾️◾️️◾️◾️
  // ◾▫️️▫️️▫️️▫️️▫️▫️️️◾️
  // ◾◾️️️◾◾️️◾️◾️️◾️◾
  // ◾️▫️️️▫️️▫️️▫️️️▫️▫️◾
  // ◾▫️◾️️️️️◾◾️️️️◾️️️️◾◾
  // ◾▫️◾️️️️️◾◾️️️️◾️️️◾️◾
  // ◾▫️◾️️️️️◾◾️️️️◾️️️◾️◾
  // ◾◾️️️◾◾️️◾️◾️️◾️◾
  ROTATED_270: 6, // 시계방향으로 270도 회전

  // ◾️️◾◾️️️◾◾️️◾️◾️◾
  // ◾▫️️️◾▫️▫️▫️▫️️️️️️◾
  // ◾️️▫️◾️️▫️◾️️◾️️◾️️◾️
  // ▫️️️▫️️◾▫️️◾️️️️️◾️️◾️◾
  // ◾️️▫️◾️️▫️◾️️◾️️◾️️◾️
  // ◾️️▫️◾️️▫️◾️️◾️️◾️️◾️
  // ◾️️▫️◾️️▫️◾️️◾️️◾️️◾️
  // ◾️️◾◾️️️◾◾️️◾️◾️◾
  INVERTED_0: 2, // 좌우 반전

  // ◾◾️️◾️️️️️◾▫️️◾️◾️️◾️
  // ◾▫️️️▫️▫️️▫️️▫️️▫️️◾️
  // ◾◾️️◾️️️◾◾️️◾️◾️️◾
  // ◾️️▫️▫️️️▫️️▫️️▫️️▫️◾
  // ◾◾️️◾️️️️️◾◾️️️️◾▫️️◾️
  // ◾◾◾️️️️️️️◾◾️️️️◾▫️️◾️
  // ◾◾️️️◾️️️️◾◾️️️️◾▫️️◾️
  // ◾◾️️️◾◾️️◾️️◾️◾️️◾
  INVERTED_90: 7, // 좌우 반전 후 시계방향으로 90도 회전

  // ◾️️◾◾️️️◾◾️️◾️◾️◾
  // ◾️️◾◾️️️️️◾▫️️◾▫️️◾
  // ◾️◾◾️️️◾▫️◾▫️️◾️
  // ◾️◾◾️️️◾▫️◾▫️️◾️
  // ◾️️◾◾️️️️️◾▫️️◾▫️▫️
  // ◾️️◾◾️️️️️◾▫️️◾▫️️◾
  // ◾️️▫️️▫️️▫️️▫️◾️️️️️️️▫️️◾
  // ◾️️◾◾️️️◾◾️️◾️◾️◾
  INVERTED_180: 4, // 좌우 반전 후 시계방향으로 180도 회전

  // ◾◾️️️◾◾️️◾️◾️️◾◾
  // ◾▫️◾️️️️️◾◾️️️️◾◾️️️◾
  // ◾▫️◾️️️️️◾◾️️️️◾️◾️️◾
  // ◾▫️◾️️️️️◾◾️️️️◾️️◾️◾
  // ◾️▫️️️▫️▫️️️▫️️▫️️▫️◾
  // ◾◾️️️◾◾️️◾️◾️️◾◾
  // ◾▫️️▫️️▫️️️▫️▫️️▫️️◾️
  // ◾◾️️️️️◾▫️️◾️◾️️◾◾️
  INVERTED_270: 5, // 좌우 반전 후 시계방향으로 270도 회전
};

export const rotateFromOrientation = (context: CanvasRenderingContext2D, width: number, height: number, orientation: number) => {
  switch (orientation) {
    case ExifOrientations.ROTATED_90:
      context.rotate(0.5 * Math.PI);
      context.translate(-width, 0);
      break;
    case ExifOrientations.ROTATED_180:
      context.translate(0, height);
      context.scale(-1, 1);
      break;
    case ExifOrientations.ROTATED_270:
      context.rotate(0.5 * Math.PI);
      context.translate(0, -height);
      break;
    case ExifOrientations.INVERTED_0:
      context.translate(width, 0);
      context.scale(-1, 1);
      break;
    case ExifOrientations.INVERTED_90:
      context.rotate(0.5 * Math.PI);
      context.translate(width, -height);
      context.scale(-1, 1);
      break;
    case ExifOrientations.INVERTED_180:
      context.translate(width, height);
      context.rotate(Math.PI);
      break;
    case ExifOrientations.INVERTED_270:
      context.rotate(0.5 * Math.PI);
      context.scale(1, -1);
      break;
    default:
      break;
  }
  return context;
};

export const rotateImage = (image: HTMLImageElement) => {
  const canvas: HTMLCanvasElement = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return null;
  const info = EXIF.getTag(image, 'Orientation');
  console.log(`orientation: ${info}`);
  const orientation = info.Orientation;
  const { width, height } = canvas;
  rotateFromOrientation(context, width, height, orientation);
  context.drawImage(image, 0, 0, width, height);
  return canvas.toDataURL(`image/jpeg`, 100);
};

// TODO: Implement it
export const rotate = (file: File) => {
  return new Promise<Blob>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    const image = new Image();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        image.src = reader.result;
        image.onload = () => {
          EXIF.getData(image.src, () => {
            console.log(EXIF.getTag(image, 'Orientation'));
            const rotated = base64ToBlob(rotateImage(image));
            if (rotated) {
              resolve(rotated);
            } else {
              reject();
            }
          });
        };
      }
    };
  });
};

const findY = (blockHeight: number, totalLineSize: number, startY: number, line: number) => {
  const y = blockHeight - startY;
  const lineHeight = y / totalLineSize;
  return startY + lineHeight * line;
};

// TODO: 구조화
export const makePdf = (
  objection: ptax.IPbTaxNotice | undefined | null,
  company: pcompany.IPbCompany | undefined | null,
  font: PdfDocumentFont = FONT_NANUM_GOTHIC,
) =>
  new Promise<JsPDF>((resolve, reject) => {
    if (!objection || !company) return reject();
    const pdf = new JsPDF();

    /**
     * !important
     * 한글 폰트가 깨지는 현상을 방지하기 위해 반드시 한글 폰트를 Base64 형태로 추가해줘야 한다.
     */
    pdf.addFileToVFS(font.filename, font.data);
    pdf.addFont(font.filename, font.name, font.style);
    pdf.setFont(font.name, font.style);

    const title = `${company.name}`;
    const desc = `${company.addr1} ${company.addr2 || ''}`;
    const receiver = `수신: ${objection.sender}`;
    const subject = '제목: 위반사실 통지서 명의변경 요청';
    const sender = `${company.name} 대표`;
    const width = pdf.internal.pageSize.width || pdf.internal.pageSize.getWidth();
    const height = pdf.internal.pageSize.height || pdf.internal.pageSize.getHeight();
    const wCenter = width / 2;
    const vCenter = height / 2;

    const blockHeight = objection.contract ? 110 : 150;
    const startY = 90;

    pdf.setFontSize(20);
    pdf.text(title, wCenter - pdf.getTextWidth(title) / 2, 30);
    pdf.setFontSize(10);
    pdf.text(desc, wCenter - pdf.getTextWidth(desc) / 2, 40);
    pdf.setLineWidth(0.8);
    pdf.line(20, 54, width - 20, 54);
    pdf.text(receiver, 30, 61);
    pdf.text(subject, 30, 71);
    pdf.line(20, 76, width - 20, 76);

    pdf.text('1. 귀서의 노고에 진심으로 감사드립니다.', 30, findY(blockHeight, 3, startY, 0));
    pdf.text(
      '2. 귀청 관내에서 적발되어 고지된건에 대하여 계약서 기재된 임차인에게 정정부과하여 주시기 바랍니다.',
      30,
      findY(blockHeight, 3, startY, 1),
    );
    pdf.text('3. 근거: 1995. 01. 05. 법률 제 4872호 도로교통법 개정 동법 제 115조 제 7항', 30, findY(blockHeight, 3, startY, 2));

    pdf.setFontSize(9);

    if (objection.contract) {
      pdf.setLineWidth(0.3);
      pdf.line(35, 124, width - 35, 124);
      pdf.line(35, 134, width - 35, 134);
      pdf.line(35, 144, width - 35, 144);
      pdf.line(35, 154, width - 35, 154);
      pdf.line(35, 164, width - 35, 164);
      pdf.line(35, 174, width - 35, 174);
      pdf.line(35, 184, width - 35, 184);
      pdf.line(35, 194, width - 35, 194);
      pdf.line(35, 214, width - 35, 214);
      pdf.line(35, 224, width - 35, 224);
      pdf.line(35, 234, width - 35, 234);

      pdf.line(35, 124, 35, 234);
      pdf.line(65, 124, 65, 234);
      pdf.line(width - 35, 124, width - 35, 234);

      pdf.text('위반차량', 50 - pdf.getTextWidth('위반차량') / 2, 130);
      pdf.text(objection.plateNo, 70, 130);
      pdf.text('위반일시', 50 - pdf.getTextWidth('위반일시') / 2, 140);
      pdf.text(moment(first(objection.violation)?.violationAt).format(FORMAT_DATE_EXCEPT_SECOND_KR), 70, 140);
      pdf.text('위반장소', 50 - pdf.getTextWidth('위반장소') / 2, 150);
      pdf.text(first(objection.violation)?.location, 70, 150);
      pdf.text('위반내용', 50 - pdf.getTextWidth('위반내용') / 2, 160);
      pdf.text(first(objection.violation)?.details, 70, 160);
      pdf.text('위반자(상호)', 50 - pdf.getTextWidth('위반자(상호)') / 2, 170);
      pdf.text(objection.user?.name, 70, 170);
      pdf.text('운전면허번호', 50 - pdf.getTextWidth('운전면허번호') / 2, 180);
      pdf.text(objection.user?.driverLicenseNo, 70, 180);
      pdf.text('생년월일', 50 - pdf.getTextWidth('생년월일') / 2, 190);
      pdf.text('1979-12-28', 70, 190);
      pdf.text('주소', 50 - pdf.getTextWidth('주소') / 2, 205);
      pdf.text(objection?.user?.addr1, 70, 201);
      pdf.text(objection?.user?.addr2, 70, 209);
      pdf.text('임대기간', 50 - pdf.getTextWidth('임대기간') / 2, 220);
      pdf.text(
        `${moment(objection?.contract?.startAt).format(FORMAT_DATE_FORMAL_KR)} ~ ${moment(objection?.contract?.dueAt).format(FORMAT_DATE_FORMAL_KR)}`,
        70,
        220,
      );
      pdf.text(`연락처`, 50 - pdf.getTextWidth('연락처') / 2, 230);
      pdf.text(objection.user?.contact, 70, 230);
    }

    pdf.setFontSize(10);
    pdf.text('별첨:', 40, 240);
    pdf.text('1. 위반사실 통지서(과태료 고지서) 1매', 50, 240);
    pdf.text('2. 차량 임대차 계약서 사본', 50, 250);

    pdf.setFontSize(18);
    pdf.text(sender, wCenter - pdf.getTextWidth(sender) / 2, 270);

    return resolve(pdf);
  });

export const makePdfToFile = (objection: ptax.IPbTaxNotice | undefined | null, company: pcompany.IPbCompany | undefined | null, outputName: string) =>
  new Promise<File>((resolve) =>
    makePdf(objection, company)
      .then((pdf) => pdf.output('blob'))
      .then((blob) =>
        resolve(
          new File([blob], outputName, {
            type: 'application/pdf',
            lastModified: moment.now(),
          }),
        ),
      ),
  );
